import API from "./api";

/**
 * Admin Offer Service
 * Handles all offer-related API calls for admin panel
 * All routes go through /admin/offers/*
 */
const adminOfferService = {
  // ===== CRUD OPERATIONS =====

  createOffer: (offerData) => {
    const formData = new FormData();

    // Append basic fields
    Object.keys(offerData).forEach((key) => {
      if (
        key !== "bannerImage" &&
        key !== "mobileBannerImage" &&
        key !== "iconImage"
      ) {
        if (typeof offerData[key] === "object" && offerData[key] !== null) {
          formData.append(key, JSON.stringify(offerData[key]));
        } else {
          formData.append(key, offerData[key]);
        }
      }
    });

    // Append image files
    if (offerData.bannerImage instanceof File) {
      formData.append("bannerImage", offerData.bannerImage);
    }
    if (offerData.mobileBannerImage instanceof File) {
      formData.append("mobileBannerImage", offerData.mobileBannerImage);
    }
    if (offerData.iconImage instanceof File) {
      formData.append("iconImage", offerData.iconImage);
    }

    return API.post("/admin/offers", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getOffers: (filters = {}) => API.get("/admin/offers", { params: filters }),

  getOfferById: (id) => API.get(`/admin/offers/${id}`),

  updateOffer: (id, offerData) => {
    const formData = new FormData();

    Object.keys(offerData).forEach((key) => {
      if (
        key !== "bannerImage" &&
        key !== "mobileBannerImage" &&
        key !== "iconImage"
      ) {
        if (typeof offerData[key] === "object" && offerData[key] !== null) {
          formData.append(key, JSON.stringify(offerData[key]));
        } else {
          formData.append(key, offerData[key]);
        }
      }
    });

    if (offerData.bannerImage instanceof File) {
      formData.append("bannerImage", offerData.bannerImage);
    }
    if (offerData.mobileBannerImage instanceof File) {
      formData.append("mobileBannerImage", offerData.mobileBannerImage);
    }
    if (offerData.iconImage instanceof File) {
      formData.append("iconImage", offerData.iconImage);
    }

    return API.patch(`/admin/offers/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteOffer: (id) => API.delete(`/admin/offers/${id}`),

  updateOfferStatus: (id, isActive, deactivationReason = null) =>
    API.patch(`/admin/offers/${id}/status`, { isActive, deactivationReason }),

  // ===== VALIDATION =====

  validateOfferCode: (offerCode, excludeId = null) =>
    API.post("/admin/offers/validate-code", { offerCode, excludeId }),

  testOffer: (id, testData) => API.post(`/admin/offers/${id}/test`, testData),

  // ===== ANALYTICS =====

  getOfferAnalytics: (id) => API.get(`/admin/offers/${id}/analytics`),

  getOfferUsage: (id, page = 1, limit = 20, sortBy = "-usedAt") =>
    API.get(`/admin/offers/${id}/usage`, {
      params: { page, limit, sortBy },
    }),

  getCustomerUsage: (id) => API.get(`/admin/offers/${id}/customer-usage`),

  getBranchUsage: (id) => API.get(`/admin/offers/${id}/branch-usage`),

  // ===== APPROVAL WORKFLOW =====

  approveOffer: (id, comments = null) =>
    API.post(`/admin/offers/${id}/approve`, { comments }),

  rejectOffer: (id, reason) =>
    API.post(`/admin/offers/${id}/reject`, { reason }),

  // ✅ FIXED: Match backend route exactly
  getPendingApprovals: () => API.get("/admin/offers/pending/approvals"),

  // ===== BULK OPERATIONS =====

  // ✅ FIXED: Match backend routes exactly (using /bulk/ with slash)
  bulkCreateOffers: (offers) =>
    API.post("/admin/offers/bulk/create", { offers }),

  bulkUpdateOffers: (updates) =>
    API.post("/admin/offers/bulk/update", { updates }),

  bulkUpdateStatus: (offerIds, isActive) =>
    API.post("/admin/offers/bulk/status", { offerIds, isActive }),

  bulkDeleteOffers: (offerIds) =>
    API.post("/admin/offers/bulk/delete", { offerIds }),

  // ===== TEMPLATES =====

  // ✅ FIXED: Match backend routes exactly
  getOfferTemplates: () => API.get("/admin/offers/templates/list"),

  createFromTemplate: (templateId, customizations) =>
    API.post(`/admin/offers/templates/${templateId}/create`, customizations),

  // ===== IMPORT/EXPORT =====

  importOffers: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return API.post("/admin/offers/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  exportOffers: (format = "xlsx", filters = {}) =>
    API.get("/admin/offers/export", {
      params: { format, ...filters },
      responseType: "blob",
    }),

  // ===== SCHEDULING =====

  scheduleOffer: (id, activationDate, deactivationDate = null) =>
    API.post(`/admin/offers/${id}/schedule`, {
      activationDate,
      deactivationDate,
    }),

  // ✅ FIXED: Match backend route exactly
  getScheduledOffers: () => API.get("/admin/offers/scheduled/list"),

  // ===== BRANCH-SPECIFIC =====

  getOffersByBranch: (branchCode, includeGeneral = true) =>
    API.get(`/admin/offers/branch/${branchCode}`, {
      params: { includeGeneral },
    }),

  cloneOffer: (id, newOfferCode, targetBranches = null, modifications = null) =>
    API.post(`/admin/offers/${id}/clone`, {
      newOfferCode,
      targetBranches,
      modifications,
    }),

  // ===== REPORTS =====

  getPerformanceReport: (params = {}) =>
    API.get("/admin/offers/reports/performance", { params }),

  getExpiringOffers: (days = 7) =>
    API.get("/admin/offers/reports/expiring", { params: { days } }),

  getPopularOffers: (limit = 10, period = "30d") =>
    API.get("/admin/offers/reports/popular", { params: { limit, period } }),
};

export default adminOfferService;
